import { ExternalLink, Settings2 } from "lucide-react";
import { Link } from "react-router";

import type { ValidationFinding } from "@/api/hooks";
import { libraryAssetHref } from "@/lib/librarySections";
import { roleProductName } from "@/lib/namedParts";
import { Button } from "@/ui/Button";

type Location = NonNullable<ValidationFinding["location"]>;

/**
 * WHO OWNS what a clinical-dependency finding names, and the one control it
 * may open (#1084).
 *
 * The service attributed the finding through its own claimant table and
 * says, per caller, whether the named library item may be corrected here.
 * This component renders that verdict and adds nothing to it:
 *
 *  - `adopted_asset` + `editable` → a link to the item's named step in the
 *    Clinical library. The link names its destination (several findings on
 *    one screen must not read as identical controls), opens the step directly
 *    when the document exists, and the item's page when the document is the
 *    thing that is missing.
 *  - `adopted_asset`, not editable → the honest sentence and NO link: a link
 *    to a control this caller cannot use is the dead control ADR 0001 forbids.
 *  - `inherited` → base-pack content, an operator's, never a link.
 *  - `project_flow` → the Project's own Pathways settings, when the surface
 *    knows which Project it is on. Never a specific control: the service does
 *    not guess which declaration caused it, and neither does this.
 *  - `ambiguous` / `unassignable` / `unlocated` → stated as such.
 *
 * Names are clinician-authored and may be Hebrew inside an English sentence,
 * so each is isolated with `<bdi>` rather than left to flip the sentence.
 */
export function FindingLocation({
  location,
  projectId,
  assetName,
}: {
  location: Location;
  /** The Project this report was run for; enables the Pathways settings link. */
  projectId?: string | undefined;
  /** The adopted item's display name, when the catalog resolved it. */
  assetName?: string | null | undefined;
}) {
  switch (location.kind) {
    case "adopted_asset": {
      const label = assetName ?? location.assetId ?? "an adopted library item";
      const document =
        location.document && location.assetType
          ? roleProductName(location.assetType, location.document)
          : null;
      const href =
        location.assetType && location.assetId
          ? libraryAssetHref(location.assetType, location.assetId) +
            (location.document && !location.missing
              ? `?open=${encodeURIComponent(location.document)}`
              : "")
          : null;
      return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
          <span>
            Adopted library item{" "}
            <bdi dir="auto" className="font-medium text-foreground">
              {label}
            </bdi>
            {document ? (
              <>
                {" "}
                · {document}
                {location.missing ? " (not supplied)" : ""}
              </>
            ) : null}
            {location.archived ? " · archived" : null}
            {location.seeded ? " · seeded base canon, read-only here" : null}
          </span>
          {location.editable && href ? (
            <Button asChild variant="outline" size="sm">
              <Link to={href}>
                <ExternalLink aria-hidden="true" className="h-4 w-4" />
                <span className="sr-only">
                  Open <bdi>{label}</bdi>
                  {document ? ` › ${document}` : ""} in the Clinical library
                </span>
                <span aria-hidden="true">Open in library</span>
              </Link>
            </Button>
          ) : (
            <span className="italic">
              {/*
                ARCHIVED FIRST, because it binds at every authority: while the
                item is archived no new version can be published for it at all,
                and the restore that lifts that is admin-only — so this is the
                prerequisite, not a milder version of the sentence below.
              */}
              {location.archived
                ? "An administrator must restore it before it can be corrected"
                : location.seeded
                  ? "Adopt its current version or ask the pack operator"
                  : "A library author must correct it"}
            </span>
          )}
        </div>
      );
    }
    case "inherited":
      return (
        <p className="text-xs text-muted-foreground">
          Inherited from the base pack · not editable here
        </p>
      );
    case "project_flow":
      return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
          <span>This Project&apos;s pathway settings</span>
          {projectId ? (
            <Button asChild variant="outline" size="sm">
              <Link to={`/projects/${encodeURIComponent(projectId)}/config/pathways`}>
                <Settings2 aria-hidden="true" className="h-4 w-4" />
                <span className="sr-only">Open this Project&apos;s Pathways settings</span>
                <span aria-hidden="true">Open Pathways settings</span>
              </Link>
            </Button>
          ) : null}
        </div>
      );
    case "ambiguous":
      return (
        <p className="text-xs italic text-muted-foreground">
          More than one adopted item could own this location · not linked
        </p>
      );
    case "unassignable":
      return (
        <p className="text-xs italic text-muted-foreground">
          The engine could not name the location safely · not linked
        </p>
      );
    case "unlocated":
      return (
        <p className="text-xs italic text-muted-foreground">
          No usable location reported
        </p>
      );
    default:
      return (
        <p className="text-xs italic text-muted-foreground">
          Location kind this editor does not recognise · not linked
        </p>
      );
  }
}
